import React from 'react';
import { Helmet } from 'react-helmet-async';

const defaultSEO = {
  siteName: 'Paliacate Studio',
  defaultTitle: 'Paliacate Studio | Marketing Digital y Branding en Cuernavaca',
  defaultDescription: 'Agencia de marketing digital, branding y diseño en Cuernavaca, Morelos. +500 proyectos, 230+ clientes. Redes sociales, identidad visual, web y fotografía profesional.',
  siteUrl: 'https://paliacatestudio.com',
  defaultImage: 'https://paliacatestudio.com/branding/og-image.png',
  twitterHandle: '@paliacatestudio'
};

export const SEO = ({ 
  title, 
  description, 
  image, 
  url,
  type = 'website',
  noIndex = false 
}) => {
  const seo = {
    title: title ? `${title} | ${defaultSEO.siteName}` : defaultSEO.defaultTitle,
    description: description || defaultSEO.defaultDescription,
    image: image || defaultSEO.defaultImage,
    url: url ? `${defaultSEO.siteUrl}${url}` : defaultSEO.siteUrl
  };

  // Ensure image URL is absolute
  const imageUrl = seo.image.startsWith('http') 
    ? seo.image 
    : `${defaultSEO.siteUrl}${seo.image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook / WhatsApp */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content="es_MX" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seo.url} />
    </Helmet>
  );
};

// Pre-configured SEO for specific pages
export const PageSEO = {
  Home: () => (
    <SEO 
      title={null}
      description="Agencia de marketing digital, branding y diseño en Cuernavaca, Morelos. +500 proyectos, 230+ clientes. Redes sociales, identidad visual, web y fotografía profesional."
      url="/"
    />
  ),
  
  Casos: () => (
    <SEO 
      title="Casos de Éxito"
      description="Conoce los proyectos que hemos desarrollado para marcas que buscan destacar. Branding, fotografía, video y estrategia digital."
      url="/casos"
    />
  ),
  
  Arquitectura: () => (
    <SEO 
      title="Arquitectura Paliacate"
      description="Descubre nuestra metodología única de trabajo. Un sistema probado para transformar tu marca en un motor de crecimiento."
      url="/arquitectura-paliacate"
    />
  ),
  
  Metodo: () => (
    <SEO 
      title="Nuestro Método"
      description="Conoce el proceso estratégico de Paliacate Studio. Diagnóstico, estrategia, producción y activación para resultados medibles."
      url="/metodo"
    />
  ),
  
  Equipo: () => (
    <SEO 
      title="Nuestro Equipo"
      description="Conoce al equipo creativo detrás de Paliacate Studio. Profesionales apasionados por el branding y el marketing visual."
      url="/equipo"
    />
  ),
  
  Portal: () => (
    <SEO 
      title="Portal de Clientes"
      description="Accede a tu portal exclusivo de cliente. Revisa tus archivos, reportes y el progreso de tus proyectos."
      url="/portal"
      noIndex={true}
    />
  ),
  
  Privacidad: () => (
    <SEO 
      title="Política de Privacidad"
      description="Conoce cómo protegemos tu información personal en Paliacate Studio."
      url="/privacidad"
      noIndex={true}
    />
  ),
  
  Terminos: () => (
    <SEO 
      title="Términos de Servicio"
      description="Términos y condiciones de uso de los servicios de Paliacate Studio."
      url="/terminos"
      noIndex={true}
    />
  )
};

export default SEO;
